/**
 * API Service
 * Axios instance with authentication interceptors and typed API methods
 */

import axios, { AxiosInstance, AxiosError, InternalAxiosRequestConfig } from 'axios';
import { getAccessToken, getRefreshToken, setTokens, clearTokens } from './auth';

// Types for API responses and requests

// Auth types
export interface User {
  id: string;
  email: string;
  name: string | null;
  role: string;
  tenant_id: string;
  is_active: boolean;
  created_at: string;
}

export interface Tenant {
  id: string;
  name: string;
  slug: string;
  email: string;
  is_active: boolean;
  created_at: string;
  timezone: string;
  widget_config?: WidgetConfig;
  deposit_config?: DepositConfig;
}

export interface TokenResponse {
  access_token: string;
  refresh_token: string;
  token_type: string;
  expires_in: number;
}

export interface AuthResponse {
  user: User;
  tenant: Tenant;
  tokens: TokenResponse;
}

export interface RegisterRequest {
  business_name: string;
  email: string;
  password: string;
  industry_type: string;
}

export interface LoginRequest {
  email: string;
  password: string;
}

export interface RefreshTokenRequest {
  refresh_token: string;
}

// Service schedule types
export type DayKey = 'monday' | 'tuesday' | 'wednesday' | 'thursday' | 'friday' | 'saturday' | 'sunday';

export interface DaySchedule {
  is_closed: boolean;
  open: string | null;   // HH:MM 24-hour format, e.g. "09:00"
  close: string | null;  // HH:MM 24-hour format, e.g. "18:00"
}

export type WeeklySchedule = Record<DayKey, DaySchedule> & { is_global_schedule?: boolean };

// Service types
export interface Service {
  id: string;
  tenant_id: string;
  name: string;
  description: string | null;
  category: string | null;
  price_cents: number;
  duration_minutes: number | null;
  keywords: string[] | null;
  ai_description: string | null;
  is_active: boolean;
  display_order: number;
  image_url: string | null;
  rebooking_cadence: string | null;
  created_at: string;
  updated_at: string;
}

export interface ServiceCreate {
  name: string;
  description?: string | null;
  category?: string | null;
  price_cents: number;
  duration_minutes?: number | null;
  keywords?: string[] | null;
  ai_description?: string | null;
  image_url?: string | null;
  rebooking_cadence?: string | null;
}

export interface ServiceUpdate {
  name?: string;
  description?: string | null;
  category?: string | null;
  price_cents?: number;
  duration_minutes?: number | null;
  keywords?: string[] | null;
  ai_description?: string | null;
  is_active?: boolean;
  display_order?: number;
  image_url?: string | null;
  rebooking_cadence?: string | null;
}

export interface ServiceListResponse {
  items: Service[];
  total: number;
  page: number;
  per_page: number;
  pages: number;
}

// Appointment service item (from junction table)
export interface AppointmentServiceItem {
  service_id: string | null;
  service_name: string;
  price_cents: number;
  duration_minutes: number;
}

// Appointment types
export interface Appointment {
  id: string;
  tenant_id: string;
  conversation_id: string | null;
  service_id: string | null;
  customer_name: string;
  customer_email: string;
  customer_phone: string | null;
  scheduled_at: string;
  duration_minutes: number;
  status: 'scheduled' | 'confirmed' | 'completed' | 'cancelled' | 'no_show';
  notes: string | null;
  services?: AppointmentServiceItem[];
  created_at: string;
  updated_at: string;
}

export interface AppointmentCreate {
  service_ids?: string[] | null;
  service_id?: string | null;
  conversation_id?: string | null;
  customer_name: string;
  customer_email: string;
  customer_phone?: string | null;
  scheduled_at: string;
  duration_minutes?: number;
  status?: string;
  notes?: string | null;
}

export interface AppointmentUpdate {
  service_ids?: string[] | null;
  service_id?: string | null;
  customer_name?: string;
  customer_email?: string;
  customer_phone?: string | null;
  scheduled_at?: string;
  duration_minutes?: number;
  status?: string;
  notes?: string | null;
}

export interface AppointmentListResponse {
  items: Appointment[];
  total: number;
  page: number;
  per_page: number;
  pages: number;
}

// Settings types
export interface WidgetConfig {
  primary_color: string;
  secondary_color: string;
  button_position: 'bottom-right' | 'bottom-left' | 'inline';
  font_family: 'system' | 'elegant';
  welcome_message: string;
  business_name: string;
  business_context: string | null;
  logo_url: string | null;
  tts_voice?: string;
  tts_locale?: string;
  stt_locale?: string;
  tts_style?: string | null;
}

export interface DepositConfig {
  enabled: boolean;
  type: 'percentage' | 'fixed';
  value: number;
  min_order_amount: number;
}

export interface StripeConfig {
  stripe_publishable_key: string | null;
  stripe_secret_key: string | null;
  stripe_webhook_secret: string | null;
  is_configured: boolean;
}

export interface AzureConfig {
  azure_openai_endpoint: string | null;
  azure_openai_key: string | null;
  azure_openai_deployment: string | null;
  azure_speech_key: string | null;
  azure_speech_region: string | null;
  is_configured: boolean;
}

export interface AzureEmbeddingConfig {
  azure_embedding_endpoint: string | null;
  azure_embedding_key: string | null;
  azure_embedding_deployment: string | null;
  is_configured: boolean;
}

export interface NanoBananaConfig {
  nano_banana_api_key: string | null;
  nano_banana_model: string | null;
  is_configured: boolean;
}

export interface SmtpConfig {
  smtp_host: string | null;
  smtp_port: number | null;
  smtp_username: string | null;
  smtp_password: string | null;
  smtp_use_tls: boolean | null;
  smtp_from_address: string | null;
  smtp_from_name: string | null;
  is_configured: boolean;
}

export interface Settings {
  tenant: Tenant;
  widget_config: WidgetConfig;
  deposit_config: DepositConfig;
  integration_status: {
    stripe_configured: boolean;
    azure_openai_configured: boolean;
    azure_speech_configured: boolean;
    azure_embedding_configured: boolean;
    nano_banana_configured: boolean;
    smtp_configured: boolean;
  };
  currency: string;
  stripe_webhook_url: string | null;
}

// Analytics types
export interface DashboardStats {
  total_conversations: number;
  total_orders: number;
  total_revenue_cents: number;
  conversion_rate: number;
  average_order_value_cents: number;
  period_start: string;
  period_end: string;
  previous_period?: {
    total_conversations: number;
    total_orders: number;
    total_revenue_cents: number;
    conversion_rate: number;
  };
}

export interface FunnelMetrics {
  conversations_started: number;
  offer_presented: number;
  payment_started: number;
  payment_completed: number;
}

export interface DailyStats {
  date: string;
  conversations: number;
  orders: number;
  revenue_cents: number;
  product_orders?: number;
  product_revenue_cents?: number;
}

export interface AppointmentStats {
  total: number;
  confirmed: number;
  abandoned: number;
}

export interface ProductStats {
  total_orders: number;
  total_revenue_cents: number;
  avg_order_value_cents: number;
}

export interface TopProduct {
  product_name: string;
  order_count: number;
  units_sold: number;
  revenue_cents: number;
}

export interface DashboardResponse {
  summary: DashboardStats;
  funnel: FunnelMetrics;
  daily_stats: DailyStats[];
  appointments: AppointmentStats;
  products: ProductStats;
  top_products: TopProduct[];
}

export interface Order {
  id: string;
  order_number: string;
  status: 'pending' | 'payment_link_sent' | 'deposit_paid' | 'fully_paid' | 'cancelled' | 'refunded';
  service_name: string;
  service_price_cents: number;
  payment_type: 'deposit' | 'full' | 'balance';
  amount_cents: number;
  paid_at: string | null;
  created_at: string;
}

export interface OrdersResponse {
  orders: Order[];
  total: number;
  has_more: boolean;
}

export interface AnalyticsEvent {
  id: string;
  event_type: string;
  event_data: Record<string, unknown> | null;
  created_at: string;
}

export interface EventsResponse {
  events: AnalyticsEvent[];
  total: number;
  has_more: boolean;
}

// Customer types
export interface Customer {
  id: string;
  tenant_id: string;
  name: string;
  email: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface CustomerCreate {
  name: string;
  email: string;
}

export interface CustomerUpdate {
  name?: string;
  email?: string;
}

export interface CustomerListResponse {
  items: Customer[];
  total: number;
  page: number;
  per_page: number;
  pages: number;
}

export interface CustomerDetail extends Customer {
  appointments: Appointment[];
  orders: AdminOrder[];
}

// Collect Payment types
export interface AppointmentWithBalance {
  id: string;
  customer_name: string;
  service_name: string;
  service_price_cents: number;
  total_paid_cents: number;
  remaining_cents: number;
  scheduled_at: string;
  currency: string;
}

export interface PaymentSummary {
  appointment_id: string;
  customer_name: string;
  service_name: string;
  service_price_cents: number;
  total_paid_cents: number;
  remaining_cents: number;
  paid_orders: AdminOrder[];
}

export interface CollectPaymentRequest {
  appointment_id: string;
  payment_method?: string | null;
  notes?: string | null;
}

// Order service item (from junction table)
export interface OrderServiceItem {
  service_id: string | null;
  service_name: string;
  price_cents: number;
  duration_minutes: number;
}

// Admin Order types
export interface OrderProductItem {
  product_id: string | null;
  product_name: string;
  variant_name: string | null;
  price_cents: number;
  quantity: number;
  line_total_cents: number;
}

export interface AdminOrder {
  id: string;
  order_number: string;
  tenant_id: string;
  customer_id: string | null;
  appointment_id: string | null;
  status: 'pending' | 'paid' | 'failed' | 'refunded' | 'partially_paid' | 'cancelled';
  order_type: 'service' | 'product';
  service_name: string;
  service_price_cents: number;
  payment_type: 'deposit' | 'full' | 'balance';
  amount_cents: number;
  delivery_fee_cents: number;
  delivery_address: {
    street_address?: string;
    apartment?: string;
    city?: string;
    state?: string;
    zip_code?: string;
  } | null;
  delivery_zip_code: string | null;
  payment_method: string | null;
  transaction_reference: string | null;
  stripe_payment_intent_id: string | null;
  customer_email: string | null;
  customer_name: string | null;
  paid_at: string | null;
  notes: string | null;
  services?: OrderServiceItem[];
  products?: OrderProductItem[];
  created_at: string;
  updated_at: string;
}

export interface AdminOrderStatusUpdate {
  status: 'pending' | 'paid' | 'failed' | 'refunded' | 'partially_paid' | 'cancelled';
  paid_at?: string | null;
}

export interface CollectRemainingIntentResponse {
  client_secret: string;
  publishable_key: string;
  order_id: string;
  order_number: string;
  amount_cents: number;
  customer_name: string | null;
  service_name: string;
}

export interface AdminOrderListResponse {
  items: AdminOrder[];
  total: number;
  page: number;
  per_page: number;
  pages: number;
}

export interface AdminOrderDetail extends AdminOrder {
  customer: Customer | null;
  appointment: Appointment | null;
}

// Product Category types
export interface ProductCategory {
  id: string;
  tenant_id: string;
  name: string;
  description: string | null;
  product_count: number;
  created_at: string;
  updated_at: string;
}

export interface ProductCategoryCreate {
  name: string;
  description?: string | null;
}

export interface ProductCategoryUpdate {
  name?: string;
  description?: string | null;
}

// Product variant types
export interface ProductVariant {
  id: string;
  product_id: string;
  name: string;
  sku: string;
  price_cents: number;
  stock_in_hand: number | null;
  image_url: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface ProductVariantCreate {
  name: string;
  price: number;
  stock_in_hand?: number | null;
  image_url?: string | null;
}

export interface ProductVariantUpdate {
  name?: string;
  price?: number;
  stock_in_hand?: number | null;
  image_url?: string | null;
  is_active?: boolean;
}

// Product types
export interface Product {
  id: string;
  tenant_id: string;
  name: string;
  description: string | null;
  category_id: string;
  category_name: string;
  price_cents: number;
  sku: string;
  stock_in_hand: number | null;
  keywords: string[] | null;
  image_url: string | null;
  is_active: boolean;
  has_variants: boolean;
  variants: ProductVariant[];
  created_at: string;
  updated_at: string;
}

export interface UploadStatus {
  processed: number;
  total: number;
  created: number;
  updated: number;
  skipped: number;
  unchanged: number;
  status: 'processing' | 'completed' | 'partial_failure' | 'not_found';
  error: string | null;
}

export interface ProductCreate {
  name: string;
  description?: string | null;
  category_id: string;
  price?: number | null;
  stock_in_hand?: number | null;
  keywords?: string[] | null;
  image_url?: string | null;
  variants?: Array<{
    name: string;
    price: number;
    stock_in_hand?: number | null;
    image_url?: string | null;
  }> | null;
}

export interface ProductUpdate {
  name?: string;
  description?: string | null;
  category_id?: string;
  price?: number;
  keywords?: string[] | null;
  image_url?: string | null;
  is_active?: boolean;
}

export interface ProductListResponse {
  items: Product[];
  total: number;
  page: number;
  per_page: number;
  pages: number;
}

export interface InventoryMovement {
  id: string;
  tenant_id: string;
  product_id: string;
  variant_id: string | null;
  quantity_delta: number;
  reason: string;
  reference_id: string | null;
  created_at: string;
}

export interface InventoryMovementCreate {
  product_id: string;
  variant_id?: string | null;
  quantity_delta: number;
  reason: 'inbound' | 'outbound';
  reference_id?: string | null;
}

export interface InventoryMovementListResponse {
  items: InventoryMovement[];
  total: number;
  page: number;
  per_page: number;
  pages: number;
}

// Embed types
export interface EmbedCodeResponse {
  script_tag: string;
  tenant_slug: string;
  api_base_url: string;
}

// API Response wrapper
export interface APIResponse<T> {
  success: boolean;
  data?: T;
  error?: {
    code: string;
    message: string;
    details?: Record<string, unknown>;
  };
}

// Error types
export class APIError extends Error {
  code: string;
  details?: Record<string, unknown>;
  status: number;

  constructor(message: string, code: string, status: number, details?: Record<string, unknown>) {
    super(message);
    this.name = 'APIError';
    this.code = code;
    this.status = status;
    this.details = details;
  }
}

// Create axios instance
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api/v1';

const api: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000,
});

// Flag to prevent multiple simultaneous token refresh attempts
let isRefreshing = false;
let refreshSubscribers: Array<(token: string) => void> = [];

function subscribeTokenRefresh(callback: (token: string) => void): void {
  refreshSubscribers.push(callback);
}

function onTokenRefreshed(token: string): void {
  refreshSubscribers.forEach((callback) => callback(token));
  refreshSubscribers = [];
}

// Request interceptor to add Authorization header
api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = getAccessToken();
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    // Cache-bust GET requests. The API sends no Cache-Control header, so the
    // browser heuristically caches list responses and serves a stale list on
    // the refetch right after a create/update/delete (e.g. a newly added
    // service wouldn't appear until a full page reload). A unique param per
    // request guarantees a fresh response without relying on backend headers
    // or triggering an extra CORS preflight.
    if ((config.method ?? 'get').toLowerCase() === 'get') {
      config.params = { ...(config.params as Record<string, unknown>), _ts: Date.now() };
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling and token refresh
api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError<APIResponse<unknown>>) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

    // Handle 401 Unauthorized - attempt token refresh
    if (error.response?.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        // Wait for the current refresh to complete
        return new Promise((resolve) => {
          subscribeTokenRefresh((token: string) => {
            if (originalRequest.headers) {
              originalRequest.headers.Authorization = `Bearer ${token}`;
            }
            resolve(api(originalRequest));
          });
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      const refreshToken = getRefreshToken();
      if (!refreshToken) {
        clearTokens();
        window.location.href = '/login';
        return Promise.reject(error);
      }

      try {
        const response = await axios.post<APIResponse<TokenResponse>>(
          `${API_BASE_URL}/admin/auth/refresh`,
          { refresh_token: refreshToken },
          { headers: { 'Content-Type': 'application/json' } }
        );

        if (response.data.success && response.data.data) {
          const { access_token, refresh_token } = response.data.data;
          setTokens(access_token, refresh_token);
          onTokenRefreshed(access_token);

          if (originalRequest.headers) {
            originalRequest.headers.Authorization = `Bearer ${access_token}`;
          }
          return api(originalRequest);
        } else {
          throw new Error('Token refresh failed');
        }
      } catch (refreshError) {
        clearTokens();
        window.location.href = '/login';
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    // Extract error details from response
    const errorResponse = error.response?.data as (APIResponse<unknown> & { detail?: string }) | undefined;
    const message = errorResponse?.error?.message || errorResponse?.detail || error.message || 'An unexpected error occurred';
    const code = errorResponse?.error?.code || 'UNKNOWN_ERROR';
    const status = error.response?.status || 500;
    const details = errorResponse?.error?.details;

    return Promise.reject(new APIError(message, code, status, details));
  }
);

// API Methods

// Auth API
export const authAPI = {
  register: async (data: RegisterRequest): Promise<AuthResponse> => {
    const response = await api.post<APIResponse<AuthResponse>>('/admin/auth/register', data);
    if (!response.data.success || !response.data.data) {
      throw new APIError('Registration failed', 'REGISTRATION_FAILED', 400);
    }
    return response.data.data;
  },

  login: async (data: LoginRequest): Promise<AuthResponse> => {
    const response = await api.post<APIResponse<AuthResponse>>('/admin/auth/login', data);
    if (!response.data.success || !response.data.data) {
      throw new APIError('Login failed', 'LOGIN_FAILED', 401);
    }
    return response.data.data;
  },

  refresh: async (refreshToken: string): Promise<TokenResponse> => {
    const response = await api.post<APIResponse<TokenResponse>>('/admin/auth/refresh', {
      refresh_token: refreshToken,
    });
    if (!response.data.success || !response.data.data) {
      throw new APIError('Token refresh failed', 'REFRESH_FAILED', 401);
    }
    return response.data.data;
  },

  logout: async (): Promise<void> => {
    try {
      await api.post<APIResponse<{ message: string }>>('/admin/auth/logout');
    } catch (error) {
      // Ignore logout errors - clear tokens anyway
      console.warn('Logout request failed, clearing tokens locally');
    }
    clearTokens();
  },

  getCurrentUser: async (): Promise<AuthResponse> => {
    const response = await api.get<APIResponse<AuthResponse>>('/admin/auth/me');
    if (!response.data.success || !response.data.data) {
      throw new APIError('Failed to get current user', 'USER_NOT_FOUND', 401);
    }
    return response.data.data;
  },
};

// Services API
export const servicesAPI = {
  list: async (params?: {
    search?: string;
    include_inactive?: boolean;
    page?: number;
    per_page?: number;
  }): Promise<ServiceListResponse> => {
    const response = await api.get<APIResponse<ServiceListResponse>>('/admin/services', {
      params: { include_inactive: true, ...params },
    });
    if (!response.data.success || !response.data.data) {
      throw new APIError('Failed to fetch services', 'FETCH_SERVICES_FAILED', 500);
    }
    return response.data.data;
  },

  get: async (id: string): Promise<Service> => {
    const response = await api.get<APIResponse<Service>>(`/admin/services/${id}`);
    if (!response.data.success || !response.data.data) {
      throw new APIError('Service not found', 'SERVICE_NOT_FOUND', 404);
    }
    return response.data.data;
  },

  create: async (data: ServiceCreate): Promise<Service> => {
    const response = await api.post<APIResponse<Service>>('/admin/services', data);
    if (!response.data.success || !response.data.data) {
      throw new APIError('Failed to create service', 'CREATE_SERVICE_FAILED', 400);
    }
    return response.data.data;
  },

  update: async (id: string, data: ServiceUpdate): Promise<Service> => {
    const response = await api.patch<APIResponse<Service>>(`/admin/services/${id}`, data);
    if (!response.data.success || !response.data.data) {
      throw new APIError('Failed to update service', 'UPDATE_SERVICE_FAILED', 400);
    }
    return response.data.data;
  },

  delete: async (id: string): Promise<void> => {
    const response = await api.delete<APIResponse<{ message: string }>>(`/admin/services/${id}`);
    if (!response.data.success) {
      throw new APIError('Failed to delete service', 'DELETE_SERVICE_FAILED', 400);
    }
  },

  permanentDelete: async (id: string): Promise<void> => {
    await api.delete(`/admin/services/${id}/permanent`);
  },

  uploadImage: async (file: File): Promise<{ image_url: string }> =>
    mediaAPI.uploadImage(file, 'services'),

  deleteImage: async (imageUrl: string): Promise<void> =>
    mediaAPI.deleteImage(imageUrl),
};

// Shared image upload/delete, reused by services and products. `kind` becomes the storage
// key prefix (services/… vs products/…) and is validated server-side against an allowlist.
export const mediaAPI = {
  uploadImage: async (
    file: File,
    kind: 'services' | 'products',
  ): Promise<{ image_url: string }> => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post<{ image_url: string }>(
      `/admin/media/upload-image?kind=${kind}`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    );
    return response.data;
  },

  deleteImage: async (imageUrl: string): Promise<void> => {
    await api.delete('/admin/media/delete-image', { data: { image_url: imageUrl } });
  },
};

// Appointments API
export const appointmentsAPI = {
  list: async (params?: {
    status?: string;
    date_from?: string;
    date_to?: string;
    service_id?: string;
    search?: string;
    page?: number;
    per_page?: number;
  }): Promise<AppointmentListResponse> => {
    const response = await api.get<APIResponse<AppointmentListResponse>>('/admin/appointments', {
      params,
    });
    if (!response.data.success || !response.data.data) {
      throw new APIError('Failed to fetch appointments', 'FETCH_APPOINTMENTS_FAILED', 500);
    }
    return response.data.data;
  },

  get: async (id: string): Promise<Appointment> => {
    const response = await api.get<APIResponse<Appointment>>(`/admin/appointments/${id}`);
    if (!response.data.success || !response.data.data) {
      throw new APIError('Appointment not found', 'APPOINTMENT_NOT_FOUND', 404);
    }
    return response.data.data;
  },

  create: async (data: AppointmentCreate): Promise<Appointment> => {
    const response = await api.post<APIResponse<Appointment>>('/admin/appointments', data);
    if (!response.data.success || !response.data.data) {
      throw new APIError('Failed to create appointment', 'CREATE_APPOINTMENT_FAILED', 400);
    }
    return response.data.data;
  },

  update: async (id: string, data: AppointmentUpdate): Promise<Appointment> => {
    const response = await api.patch<APIResponse<Appointment>>(`/admin/appointments/${id}`, data);
    if (!response.data.success || !response.data.data) {
      throw new APIError('Failed to update appointment', 'UPDATE_APPOINTMENT_FAILED', 400);
    }
    return response.data.data;
  },

  delete: async (id: string): Promise<void> => {
    await api.delete(`/admin/appointments/${id}`);
    // 204 No Content — no body to parse; axios throws automatically on non-2xx
  },
};

// Settings API
export const settingsAPI = {
  get: async (): Promise<Settings> => {
    const response = await api.get<APIResponse<Settings>>('/admin/settings');
    if (!response.data.success || !response.data.data) {
      throw new APIError('Failed to fetch settings', 'FETCH_SETTINGS_FAILED', 500);
    }
    return response.data.data;
  },

  updateWidget: async (config: Partial<WidgetConfig>): Promise<WidgetConfig> => {
    const response = await api.patch<APIResponse<{ widget_config: WidgetConfig }>>('/admin/settings/widget', config);
    if (!response.data.success || !response.data.data) {
      throw new APIError('Failed to update widget settings', 'UPDATE_WIDGET_FAILED', 400);
    }
    return response.data.data.widget_config;
  },

  updateDeposit: async (config: Partial<DepositConfig>): Promise<DepositConfig> => {
    const response = await api.patch<APIResponse<DepositConfig>>('/admin/settings/deposit', config);
    if (!response.data.success || !response.data.data) {
      throw new APIError('Failed to update deposit settings', 'UPDATE_DEPOSIT_FAILED', 400);
    }
    return response.data.data;
  },

  updateStripe: async (config: {
    stripe_publishable_key?: string;
    stripe_secret_key?: string;
    stripe_webhook_secret?: string;
  }): Promise<StripeConfig> => {
    const response = await api.patch<APIResponse<StripeConfig>>('/admin/settings/stripe', config);
    if (!response.data.success || !response.data.data) {
      throw new APIError('Failed to update Stripe settings', 'UPDATE_STRIPE_FAILED', 400);
    }
    return response.data.data;
  },

  updateAzure: async (config: {
    azure_openai_endpoint?: string;
    azure_openai_key?: string;
    azure_openai_deployment?: string;
    azure_speech_key?: string;
    azure_speech_region?: string;
  }): Promise<AzureConfig> => {
    const response = await api.patch<APIResponse<AzureConfig>>('/admin/settings/azure', config);
    if (!response.data.success || !response.data.data) {
      throw new APIError('Failed to update Azure settings', 'UPDATE_AZURE_FAILED', 400);
    }
    return response.data.data;
  },

  updateAzureEmbedding: async (config: {
    azure_embedding_endpoint?: string;
    azure_embedding_key?: string;
    azure_embedding_deployment?: string;
  }): Promise<AzureEmbeddingConfig> => {
    const response = await api.patch<APIResponse<AzureEmbeddingConfig>>('/admin/settings/azure-embedding', config);
    if (!response.data.success || !response.data.data) {
      throw new APIError('Failed to update Azure embedding settings', 'UPDATE_AZURE_EMBEDDING_FAILED', 400);
    }
    return response.data.data;
  },

  updateNanoBanana: async (config: {
    nano_banana_api_key?: string;
    nano_banana_model?: string;
  }): Promise<NanoBananaConfig> => {
    const response = await api.patch<APIResponse<NanoBananaConfig>>('/admin/settings/nano-banana', config);
    if (!response.data.success || !response.data.data) {
      throw new APIError('Failed to update Nano Banana settings', 'UPDATE_NANO_BANANA_FAILED', 400);
    }
    return response.data.data;
  },

  updateSmtp: async (config: {
    smtp_host?: string;
    smtp_port?: number;
    smtp_username?: string;
    smtp_password?: string;
    smtp_use_tls?: boolean;
    smtp_from_address?: string;
    smtp_from_name?: string;
  }): Promise<SmtpConfig> => {
    const response = await api.patch<APIResponse<SmtpConfig>>('/admin/settings/smtp', config);
    if (!response.data.success || !response.data.data) {
      throw new APIError('Failed to update SMTP settings', 'UPDATE_SMTP_FAILED', 400);
    }
    return response.data.data;
  },

  getBusinessHours: async (): Promise<{ business_hours: WeeklySchedule | null; timezone: string; booking_buffer_minutes: number }> => {
    const response = await api.get<APIResponse<{ business_hours: WeeklySchedule | null; timezone: string; booking_buffer_minutes: number }>>('/admin/settings/business-hours');
    if (!response.data.success || !response.data.data) {
      throw new APIError('Failed to fetch business hours', 'FETCH_BUSINESS_HOURS_FAILED', 500);
    }
    return response.data.data;
  },

  updateBusinessHours: async (data: { business_hours?: WeeklySchedule | null; timezone?: string; booking_buffer_minutes?: number }): Promise<{ business_hours: WeeklySchedule | null; timezone: string; booking_buffer_minutes: number }> => {
    const response = await api.patch<APIResponse<{ business_hours: WeeklySchedule | null; timezone: string; booking_buffer_minutes: number }>>('/admin/settings/business-hours', data);
    if (!response.data.success || !response.data.data) {
      throw new APIError('Failed to update business hours', 'UPDATE_BUSINESS_HOURS_FAILED', 400);
    }
    return response.data.data;
  },

  updateCurrency: async (currency: string): Promise<{ currency: string }> => {
    const response = await api.patch<APIResponse<{ currency: string }>>('/admin/settings/currency', { currency });
    if (!response.data.success || !response.data.data) {
      throw new APIError('Failed to update currency', 'UPDATE_CURRENCY_FAILED', 400);
    }
    return response.data.data;
  },
};

// Analytics API
export const analyticsAPI = {
  getDashboard: async (params?: {
    start_date?: string;
    end_date?: string;
    period?: '7d' | '30d' | '90d' | 'custom';
  }): Promise<DashboardResponse> => {
    const response = await api.get<APIResponse<DashboardResponse>>('/admin/analytics/dashboard', {
      params,
    });
    if (!response.data.success || !response.data.data) {
      throw new APIError('Failed to fetch dashboard data', 'FETCH_DASHBOARD_FAILED', 500);
    }
    return response.data.data;
  },

  getOrders: async (params?: {
    page?: number;
    page_size?: number;
    status?: string;
    start_date?: string;
    end_date?: string;
  }): Promise<OrdersResponse> => {
    const response = await api.get<APIResponse<OrdersResponse>>('/admin/analytics/orders', {
      params,
    });
    if (!response.data.success || !response.data.data) {
      throw new APIError('Failed to fetch orders', 'FETCH_ORDERS_FAILED', 500);
    }
    return response.data.data;
  },

  getEvents: async (params?: {
    limit?: number;
    offset?: number;
    event_type?: string;
    start_date?: string;
    end_date?: string;
  }): Promise<EventsResponse> => {
    const response = await api.get<APIResponse<EventsResponse>>('/admin/analytics/events', {
      params,
    });
    if (!response.data.success || !response.data.data) {
      throw new APIError('Failed to fetch events', 'FETCH_EVENTS_FAILED', 500);
    }
    return response.data.data;
  },

};

// Embed API
export const embedAPI = {
  getEmbedCode: async (): Promise<EmbedCodeResponse> => {
    const response = await api.get<APIResponse<EmbedCodeResponse>>('/admin/embed');
    if (!response.data.success || !response.data.data) {
      throw new APIError('Failed to get embed code', 'FETCH_EMBED_FAILED', 500);
    }
    return response.data.data;
  },
};

// Customers API
export const customersAPI = {
  list: async (params?: {
    search?: string;
    email?: string;
    is_active?: boolean;
    sort_by?: string;
    sort_order?: string;
    page?: number;
    per_page?: number;
  }): Promise<CustomerListResponse> => {
    const response = await api.get<APIResponse<CustomerListResponse>>('/admin/customers', {
      params,
    });
    if (!response.data.success || !response.data.data) {
      throw new APIError('Failed to fetch customers', 'FETCH_CUSTOMERS_FAILED', 500);
    }
    return response.data.data;
  },

  get: async (id: string, include?: Array<'appointments' | 'orders'>): Promise<CustomerDetail> => {
    const response = await api.get<APIResponse<CustomerDetail>>(`/admin/customers/${id}`, {
      params: include?.length ? { include } : undefined,
    });
    if (!response.data.success || !response.data.data) {
      throw new APIError('Customer not found', 'CUSTOMER_NOT_FOUND', 404);
    }
    return response.data.data;
  },

  create: async (data: CustomerCreate): Promise<Customer> => {
    const response = await api.post<APIResponse<Customer>>('/admin/customers', data);
    if (!response.data.success || !response.data.data) {
      throw new APIError('Failed to create customer', 'CREATE_CUSTOMER_FAILED', 400);
    }
    return response.data.data;
  },

  update: async (id: string, data: CustomerUpdate): Promise<Customer> => {
    const response = await api.patch<APIResponse<Customer>>(`/admin/customers/${id}`, data);
    if (!response.data.success || !response.data.data) {
      throw new APIError('Failed to update customer', 'UPDATE_CUSTOMER_FAILED', 400);
    }
    return response.data.data;
  },

  activate: async (id: string): Promise<Customer> => {
    const response = await api.patch<APIResponse<Customer>>(`/admin/customers/${id}/activate`);
    if (!response.data.success || !response.data.data) {
      throw new APIError('Failed to activate customer', 'ACTIVATE_CUSTOMER_FAILED', 400);
    }
    return response.data.data;
  },

  delete: async (id: string): Promise<void> => {
    await api.delete(`/admin/customers/${id}`);
  },
};

// Admin Orders API
export const adminOrdersAPI = {
  list: async (params?: {
    status?: string;
    payment_type?: string;
    order_type?: string;
    search?: string;
    customer_id?: string;
    appointment_id?: string;
    date_from?: string;
    date_to?: string;
    sort_by?: string;
    sort_order?: string;
    page?: number;
    per_page?: number;
  }): Promise<AdminOrderListResponse> => {
    const response = await api.get<APIResponse<AdminOrderListResponse>>('/admin/orders', {
      params,
    });
    if (!response.data.success || !response.data.data) {
      throw new APIError('Failed to fetch orders', 'FETCH_ORDERS_FAILED', 500);
    }
    return response.data.data;
  },

  get: async (id: string): Promise<AdminOrderDetail> => {
    const response = await api.get<APIResponse<AdminOrderDetail>>(`/admin/orders/${id}`);
    if (!response.data.success || !response.data.data) {
      throw new APIError('Order not found', 'ORDER_NOT_FOUND', 404);
    }
    return response.data.data;
  },

  updateStatus: async (id: string, data: AdminOrderStatusUpdate): Promise<AdminOrder> => {
    const response = await api.patch<APIResponse<AdminOrder>>(`/admin/orders/${id}/status`, data);
    if (!response.data.success || !response.data.data) {
      throw new APIError('Failed to update order status', 'UPDATE_ORDER_STATUS_FAILED', 400);
    }
    return response.data.data;
  },

  getAppointmentsWithBalance: async (): Promise<AppointmentWithBalance[]> => {
    const response = await api.get<APIResponse<AppointmentWithBalance[]>>('/admin/orders/appointments-with-balance');
    if (!response.data.success || !response.data.data) {
      throw new APIError('Failed to fetch appointments with balance', 'FETCH_APPOINTMENTS_BALANCE_FAILED', 500);
    }
    return response.data.data;
  },

  getPaymentSummary: async (appointmentId: string): Promise<PaymentSummary> => {
    const response = await api.get<APIResponse<PaymentSummary>>(`/admin/orders/payment-summary/${appointmentId}`);
    if (!response.data.success || !response.data.data) {
      throw new APIError('Failed to fetch payment summary', 'FETCH_PAYMENT_SUMMARY_FAILED', 500);
    }
    return response.data.data;
  },

  createBalanceOrder: async (appointmentId: string): Promise<AdminOrder> => {
    const response = await api.post<APIResponse<AdminOrder>>(`/admin/orders/create-balance-order/${appointmentId}`);
    if (!response.data.success || !response.data.data) {
      throw new APIError('Failed to create balance order', 'CREATE_BALANCE_ORDER_FAILED', 500);
    }
    return response.data.data;
  },

  collectPayment: async (data: CollectPaymentRequest): Promise<AdminOrder> => {
    const response = await api.post<APIResponse<AdminOrder>>('/admin/orders/collect-remaining', data);
    if (!response.data.success || !response.data.data) {
      throw new APIError('Failed to collect payment', 'COLLECT_PAYMENT_FAILED', 400);
    }
    return response.data.data;
  },

  verifyPayment: async (orderId: string): Promise<AdminOrder> => {
    const response = await api.post<APIResponse<AdminOrder>>(`/admin/orders/${orderId}/verify-payment`);
    if (!response.data.success || !response.data.data) {
      throw new APIError('Payment verification failed', 'VERIFY_PAYMENT_FAILED', 400);
    }
    return response.data.data;
  },

  createCollectRemainingIntent: async (appointmentId: string): Promise<CollectRemainingIntentResponse> => {
    const response = await api.post<APIResponse<CollectRemainingIntentResponse>>(
      '/admin/orders/collect-remaining-intent',
      { appointment_id: appointmentId },
    );
    if (!response.data.success || !response.data.data) {
      throw new APIError('Failed to create payment intent', 'CREATE_INTENT_FAILED', 400);
    }
    return response.data.data;
  },
};

// Product Categories API
export const productCategoriesAPI = {
  list: async (): Promise<ProductCategory[]> => {
    const response = await api.get<APIResponse<ProductCategory[]>>('/admin/product-categories');
    if (!response.data.success || !response.data.data) {
      throw new APIError('Failed to fetch categories', 'FETCH_CATEGORIES_FAILED', 500);
    }
    return response.data.data;
  },

  get: async (id: string): Promise<ProductCategory> => {
    const response = await api.get<APIResponse<ProductCategory>>(`/admin/product-categories/${id}`);
    if (!response.data.success || !response.data.data) {
      throw new APIError('Category not found', 'CATEGORY_NOT_FOUND', 404);
    }
    return response.data.data;
  },

  create: async (data: ProductCategoryCreate): Promise<ProductCategory> => {
    const response = await api.post<APIResponse<ProductCategory>>('/admin/product-categories', data);
    if (!response.data.success || !response.data.data) {
      throw new APIError('Failed to create category', 'CREATE_CATEGORY_FAILED', 400);
    }
    return response.data.data;
  },

  update: async (id: string, data: ProductCategoryUpdate): Promise<ProductCategory> => {
    const response = await api.patch<APIResponse<ProductCategory>>(`/admin/product-categories/${id}`, data);
    if (!response.data.success || !response.data.data) {
      throw new APIError('Failed to update category', 'UPDATE_CATEGORY_FAILED', 400);
    }
    return response.data.data;
  },

  delete: async (id: string): Promise<void> => {
    await api.delete(`/admin/product-categories/${id}`);
  },
};

// Products API
export const productsAPI = {
  list: async (params?: {
    category_id?: string;
    search?: string;
    include_inactive?: boolean;
    page?: number;
    per_page?: number;
  }): Promise<ProductListResponse> => {
    const response = await api.get<APIResponse<ProductListResponse>>('/admin/products', {
      params: { include_inactive: true, ...params },
    });
    if (!response.data.success || !response.data.data) {
      throw new APIError('Failed to fetch products', 'FETCH_PRODUCTS_FAILED', 500);
    }
    return response.data.data;
  },

  get: async (id: string): Promise<Product> => {
    const response = await api.get<APIResponse<Product>>(`/admin/products/${id}`);
    if (!response.data.success || !response.data.data) {
      throw new APIError('Product not found', 'PRODUCT_NOT_FOUND', 404);
    }
    return response.data.data;
  },

  create: async (data: ProductCreate): Promise<Product> => {
    const response = await api.post<APIResponse<Product>>('/admin/products', data);
    if (!response.data.success || !response.data.data) {
      throw new APIError('Failed to create product', 'CREATE_PRODUCT_FAILED', 400);
    }
    return response.data.data;
  },

  update: async (id: string, data: ProductUpdate): Promise<Product> => {
    const response = await api.patch<APIResponse<Product>>(`/admin/products/${id}`, data);
    if (!response.data.success || !response.data.data) {
      throw new APIError('Failed to update product', 'UPDATE_PRODUCT_FAILED', 400);
    }
    return response.data.data;
  },

  delete: async (id: string): Promise<void> => {
    await api.delete(`/admin/products/${id}`);
  },

  addInventoryMovement: async (productId: string, data: Omit<InventoryMovementCreate, 'product_id'>): Promise<InventoryMovement> => {
    const response = await api.post<APIResponse<InventoryMovement>>(`/admin/products/${productId}/inventory`, {
      ...data,
      product_id: productId,
    });
    if (!response.data.success || !response.data.data) {
      throw new APIError('Failed to add inventory movement', 'ADD_INVENTORY_FAILED', 400);
    }
    return response.data.data;
  },

  listInventoryMovements: async (productId: string, params?: {
    reason?: string;
    page?: number;
    per_page?: number;
  }): Promise<InventoryMovementListResponse> => {
    const response = await api.get<APIResponse<InventoryMovementListResponse>>(`/admin/products/${productId}/inventory`, {
      params,
    });
    if (!response.data.success || !response.data.data) {
      throw new APIError('Failed to fetch inventory movements', 'FETCH_INVENTORY_FAILED', 500);
    }
    return response.data.data;
  },

  uploadCSV: async (file: File): Promise<{ task_id: string; total_rows: number }> => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post<{ task_id: string; total_rows: number }>(
      '/admin/products/upload-csv',
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    );
    return response.data;
  },

  getUploadStatus: async (taskId: string): Promise<UploadStatus> => {
    const response = await api.get<UploadStatus>(`/admin/products/upload-status/${taskId}`);
    return response.data;
  },

  // Variant CRUD
  listVariants: async (productId: string): Promise<ProductVariant[]> => {
    const response = await api.get<APIResponse<ProductVariant[]>>(
      `/admin/products/${productId}/variants`,
    );
    if (!response.data.success || !response.data.data) {
      throw new APIError('Failed to fetch variants', 'FETCH_VARIANTS_FAILED', 500);
    }
    return response.data.data;
  },

  createVariant: async (productId: string, data: ProductVariantCreate): Promise<ProductVariant> => {
    const response = await api.post<APIResponse<ProductVariant>>(
      `/admin/products/${productId}/variants`,
      data,
    );
    if (!response.data.success || !response.data.data) {
      throw new APIError('Failed to create variant', 'CREATE_VARIANT_FAILED', 400);
    }
    return response.data.data;
  },

  updateVariant: async (
    productId: string,
    variantId: string,
    data: ProductVariantUpdate,
  ): Promise<ProductVariant> => {
    const response = await api.patch<APIResponse<ProductVariant>>(
      `/admin/products/${productId}/variants/${variantId}`,
      data,
    );
    if (!response.data.success || !response.data.data) {
      throw new APIError('Failed to update variant', 'UPDATE_VARIANT_FAILED', 400);
    }
    return response.data.data;
  },

  deleteVariant: async (productId: string, variantId: string): Promise<void> => {
    await api.delete(`/admin/products/${productId}/variants/${variantId}`);
  },
};

// Delivery Zone types
export interface DeliveryZone {
  id: string;
  tenant_id: string;
  zip_code: string;
  city: string | null;
  state: string | null;
  is_active: boolean;
  created_at: string;
}

export interface DeliveryZoneCreate {
  zip_code: string;
  city?: string;
  state?: string;
}

export interface DeliveryZoneUpdate {
  zip_code?: string;
  city?: string;
  state?: string;
  is_active?: boolean;
}

export interface DeliveryZoneListResponse {
  items: DeliveryZone[];
  total: number;
  page: number;
  per_page: number;
  pages: number;
}

// Delivery Zones API
export const deliveryZonesAPI = {
  list: async (params?: {
    search?: string;
    page?: number;
    per_page?: number;
  }): Promise<DeliveryZoneListResponse> => {
    const response = await api.get<APIResponse<DeliveryZoneListResponse>>('/admin/delivery-zip-codes', {
      params,
    });
    if (!response.data.success || !response.data.data) {
      throw new APIError('Failed to fetch delivery zones', 'FETCH_DELIVERY_ZONES_FAILED', 500);
    }
    return response.data.data;
  },

  create: async (data: DeliveryZoneCreate): Promise<DeliveryZone> => {
    const response = await api.post<APIResponse<DeliveryZone>>('/admin/delivery-zip-codes', data);
    if (!response.data.success || !response.data.data) {
      throw new APIError('Failed to create delivery zone', 'CREATE_DELIVERY_ZONE_FAILED', 400);
    }
    return response.data.data;
  },

  update: async (id: string, data: DeliveryZoneUpdate): Promise<DeliveryZone> => {
    const response = await api.patch<APIResponse<DeliveryZone>>(`/admin/delivery-zip-codes/${id}`, data);
    if (!response.data.success || !response.data.data) {
      throw new APIError('Failed to update delivery zone', 'UPDATE_DELIVERY_ZONE_FAILED', 400);
    }
    return response.data.data;
  },

  delete: async (id: string): Promise<void> => {
    await api.delete(`/admin/delivery-zip-codes/${id}`);
  },
};

// Export the axios instance for custom requests
export { api };

export default {
  auth: authAPI,
  services: servicesAPI,
  appointments: appointmentsAPI,
  settings: settingsAPI,
  analytics: analyticsAPI,
  embed: embedAPI,
  customers: customersAPI,
  adminOrders: adminOrdersAPI,
  productCategories: productCategoriesAPI,
  products: productsAPI,
  deliveryZones: deliveryZonesAPI,
};
